import { Alert } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";

import ProjectForm, { ProjectFormData } from "../../components/ProjectForm";
import useRequireCurrentProject from "../../hooks/useRequireCurrentProject";
import useProjectDelete from "./api/useProjectDelete";
import useProjectUpdate from "./api/useProjectUpdate";

export default function ProjectUpdateScreen() {
    const { project } = useRequireCurrentProject();
    const navigate = useNavigate();
    const { t } = useTranslation("project");

    const { updateProject, error: updateError } = useProjectUpdate(project.id);
    const { deleteProject, loading: deleteLoading, error: deleteError } = useProjectDelete(project.id);

    const handleSubmit = async (data: ProjectFormData) => {
        await updateProject(data);
        navigate(-1);
    };

    const handleDelete = async () => {
        await deleteProject();
        navigate("../../");
    };

    const initialData: ProjectFormData = useMemo(
        () => ({
            name: project.name,
            status: project.status,
            isSpeciesManagementPlan: project.isSpeciesManagementPlan,
            showAllObservationsToClientUser: project.showAllObservationsToClientUser,
            travelAllowanceKms: project.travelAllowanceKms,
            travelAllowancePerKmInCents: project.travelAllowancePerKmInCents,
            travelAllowanceTimeInSeconds: project.travelAllowanceTimeInSeconds,
            startAt: project.startAt,
            contactEmail: project.contactEmail,
            contactName: project.contactName,
            contactPhone: project.contactPhone,
        }),
        [project],
    );

    return (
        <GaiaPageLayoutColumn>
            <GaiaPageComponent title={project.name}>
                <ProjectForm
                    initialData={initialData}
                    onSubmit={handleSubmit}
                    error={updateError}
                    formButtonLabel={t("projectUpdateScreen.btnSubmit")}
                    isStatusChangeAllowed={project.allowedActions.changeStatus.allowed}
                    statusChangeReason={project.allowedActions.changeStatus.reason}
                />
            </GaiaPageComponent>
            <GaiaPageComponent title={t("projectUpdateScreen.dangertitle")} titleVariant="h3" sx={{ textAlign: "center", mt: 2 }}>
                {deleteError && <Alert severity="error">{deleteError.message}</Alert>}
                <GaiaDeleteButton
                    sx={{ width: "100%" }}
                    label={t("projectUpdateScreen.btnDelete")}
                    onDelete={handleDelete}
                    isLoading={deleteLoading}
                />
            </GaiaPageComponent>
        </GaiaPageLayoutColumn>
    );
}
